/**
 * AML/CTF API Service
 *
 * Wraps axios calls to /aml/* endpoints with consistent
 * error handling and response unwrapping.
 */

import axios from 'axios';

const BASE = '/aml';

// Cached doc-type config. The catalogue rarely changes during a session,
// so we hit GET /aml/config/document-types once and reuse the result for
// the wizard, PersonForm, and any later UBO/edit screens.
let _docTypeConfigPromise = null;

const amlApi = {
    async get(path, params = {}, config = {}) {
        const res = await axios.get(`${BASE}/${path}`, { params, ...config });
        return res.data;
    },

    async post(path, data = {}, config = {}) {
        const res = await axios.post(`${BASE}/${path}`, data, config);
        return res.data;
    },

    async put(path, data = {}) {
        const res = await axios.put(`${BASE}/${path}`, data);
        return res.data;
    },

    async patch(path, data = {}) {
        const res = await axios.patch(`${BASE}/${path}`, data);
        return res.data;
    },

    async delete(path) {
        const res = await axios.delete(`${BASE}/${path}`);
        return res.data;
    },

    /**
     * Fetch the AUSTRAC-anchored doc-type catalogue (categories,
     * documents with manual_fields metadata, mode labels). Cached for
     * the lifetime of the session.
     */
    async loadDocTypes() {
        if (!_docTypeConfigPromise) {
            _docTypeConfigPromise = this.get('config/document-types').then(r => r.data || r);
        }
        return _docTypeConfigPromise;
    },

    /**
     * Translate an error from a Shufti-backed call (verify-id, run-kyb,
     * checks/run, checks/retry, cdd-review/start) into a user-friendly
     * message + an optional technical detail for staff to copy when
     * raising support tickets.
     *
     * Heuristic:
     *   - 422 with errors[] → original validation message (already user-
     *     friendly: "Email is required" etc.); detail is null
     *   - Any other failure → standardised friendly Shufti message;
     *     detail carries the raw provider/system message
     *
     * Returns { message: string, detail: string|null }.
     */
    shuftiFriendlyError(err) {
        const status = err?.response?.status;
        const body = err?.response?.data || {};
        const raw = body.errors;
        const rawMsg = Array.isArray(raw) ? raw[0]
            : (typeof raw === 'string' ? raw : null);

        // Validation errors from our own controllers are already user-
        // facing. Pass through.
        if (status === 422 && rawMsg) {
            return { message: rawMsg, detail: null };
        }

        return {
            message: "We couldn't complete this verification. Please try again. If the problem persists, the verification service may be temporarily unavailable — please retry in a few minutes.",
            detail: rawMsg || err?.message || null,
        };
    },

    /**
     * Convert a File to a base64 string (without the data: prefix) so
     * verify-id payloads can ship the image inline. ~4MB before this
     * resolves; client-side resize is the caller's job.
     */
    fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result || '';
                // Strip "data:image/jpeg;base64," prefix if present
                const comma = result.indexOf(',');
                resolve(comma >= 0 ? result.slice(comma + 1) : result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    },
};

export default amlApi;
